import { useState } from 'react';
import type { ZeroIndexedPage } from 'shared-utils/types/formFiller';
import { oneIndexedPage, zeroIndexedPage } from 'shared-utils/types/formFiller';
import React, { useRef, useEffect, useCallback } from 'react';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import styled from 'styled-components';
import { MAX_PDF_WIDTH_PX } from '../../lib/formFiller';
import { PDF_UNIT_VAR_NAME } from './styles';
import * as Sentry from '@sentry/react';

// Type for pdfjsLib to support different pdfjs-dist versions
// We use "any" for version flexibility since different versions may have different type structures
type PdfjsLib = any;

// Debug logger for PDF rendering - can be disabled in production
const DEBUG_PDF_RENDERING = import.meta.env.DEV;

const debugLog = {
  render: (pageNum: number, action: string, details?: any) => {
    if (!DEBUG_PDF_RENDERING) return;
    const timestamp = new Date().toISOString().split('T')[1];
    // eslint-disable-next-line no-console
    console.log(`[PDF-RENDER ${timestamp}] Page ${pageNum + 1}: ${action}`, details || '');
  },
  queue: (action: string, details?: any) => {
    if (!DEBUG_PDF_RENDERING) return;
    const timestamp = new Date().toISOString().split('T')[1];
    // eslint-disable-next-line no-console
    console.log(`[PDF-QUEUE ${timestamp}] ${action}`, details || '');
  },
  canvas: (pageNum: number, action: string, details?: any) => {
    if (!DEBUG_PDF_RENDERING) return;
    const timestamp = new Date().toISOString().split('T')[1];
    // eslint-disable-next-line no-console
    console.log(`[PDF-CANVAS ${timestamp}] Page ${pageNum + 1}: ${action}`, details || '');
  },
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CONTAINER_PADDING_PX = 20;

const PDFContainer = styled.div`
  position: relative;
  flex: 0 1 auto;
  width: 100%;
  min-width: 0;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: ${CONTAINER_PADDING_PX}px;
  // TODO(tokens): Replace hex color value #f5f5f5 with numbered equivalent once available.
  background-color: #f5f5f5;
  box-sizing: border-box;
`;

const PDFScrollContainer = styled.div`
  position: relative;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  max-width: ${MAX_PDF_WIDTH_PX}px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  scroll-behavior: smooth;
`;

const PDFContentArea = styled.div`
  position: relative;
  width: 100%;
  max-width: ${MAX_PDF_WIDTH_PX}px;
  margin: 0 auto;
`;

const PageContainer = styled.div<{
  $pageNumber: number;
  $isFirst: boolean;
  $width?: number;
  $height?: number;
}>`
  position: relative;
  margin-top: ${({ $isFirst }) => ($isFirst ? 0 : '20px')};
  background: white;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PageCanvas = styled.canvas`
  display: block;
  /* Dimensions are set directly via JavaScript for precise control */
`;

/**
 * Handles loading a PDF and rendering each page, guarding against race conditions.
 *
 * It takes `renderOverlay` that renders a form overlay on top backed by state managed in the parent component
 *
 * @param getDocument - The pdfjs-dist getDocument function. This is injected to allow different apps to use different versions.
 */
export function PDFDisplay({
  basePdfUrl,
  renderOverlay,
  pageCount,
  setPageCount,
  pdfjsLib,
}: {
  basePdfUrl: string;
  renderOverlay: (pageIndex: ZeroIndexedPage, pageDimensions?: { width: number; height: number }) => React.ReactNode;
  pageCount: number;
  setPageCount: (pageCount: number) => void;
  pdfjsLib: PdfjsLib;
}) {
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  // Zero-indexed pages
  const [renderedPages, setRenderedPages] = useState<Set<ZeroIndexedPage>>(new Set());
  const [renderingPages, setRenderingPages] = useState<Set<ZeroIndexedPage>>(new Set());
  const [renderQueue, setRenderQueue] = useState<number[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [pageDimensions, setPageDimensions] = useState<Map<ZeroIndexedPage, { width: number; height: number }>>(
    new Map(),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<Map<ZeroIndexedPage, HTMLCanvasElement>>(new Map());
  const viewportCache = useRef<Map<ZeroIndexedPage, any>>(new Map());
  const renderPromises = useRef<Map<ZeroIndexedPage, Promise<void>>>(new Map());
  const isLoadingPdf = useRef(false);
  const loadingTask = useRef<any>(null);

  const updateFontSizes = useCallback(() => {
    if (!containerRef.current) return;

    const pdfHeight = pageDimensions.get(zeroIndexedPage(0))?.height;
    if (!pdfHeight) return;
    containerRef.current.style.setProperty(PDF_UNIT_VAR_NAME, `${pdfHeight * 0.01}px`);
  }, [pageDimensions]);

  useEffect(() => {
    updateFontSizes();
  }, [updateFontSizes]);

  // Internal render function (used by queue processor)
  const renderPageInternal = useCallback(
    async (pageNum: ZeroIndexedPage, canvas: HTMLCanvasElement) => {
      if (!pdfDocument) {
        debugLog.render(pageNum, 'PDF document not loaded, aborting render');
        return;
      }

      // Prevent concurrent rendering of the same page or already rendered pages
      if (renderingPages.has(pageNum) || renderedPages.has(pageNum)) {
        debugLog.render(pageNum, 'Skipping render - already rendering or rendered');
        return;
      }

      // Store canvas reference
      canvasRefs.current.set(pageNum, canvas);

      debugLog.render(pageNum, 'Starting render');
      setRenderingPages((prev) => new Set([...prev, pageNum]));
      let context: CanvasRenderingContext2D | null = null;

      try {
        const page = await pdfDocument.getPage(pageNum + 1); // PDF.js uses 1-based indexing

        // Use cached viewport if available, otherwise compute and cache it
        let scaledViewport;
        if (viewportCache.current.has(pageNum)) {
          scaledViewport = viewportCache.current.get(pageNum);
          debugLog.render(pageNum, 'Using cached viewport', {
            width: scaledViewport.width,
            height: scaledViewport.height,
            rotation: scaledViewport.rotation,
          });
        } else {
          // Get the page's natural properties
          const naturalViewport = page.getViewport({ scale: 1.0 });
          debugLog.render(pageNum, 'Natural viewport properties', {
            width: naturalViewport.width,
            height: naturalViewport.height,
            rotation: naturalViewport.rotation,
            transform: naturalViewport.transform,
          });

          // Calculate scale for responsive rendering with high-DPI support
          const devicePixelRatio = window.devicePixelRatio || 1;
          const scrollContainerElement = scrollContainerRef.current;
          const defaultMaxWidth = MAX_PDF_WIDTH_PX;
          const availableWidth = scrollContainerElement
            ? Math.min(scrollContainerElement.clientWidth, defaultMaxWidth)
            : defaultMaxWidth;

          // Base scale to fit container width, enhanced for high-DPI displays
          const baseScale = availableWidth / naturalViewport.width;
          const scale = baseScale * devicePixelRatio;
          scaledViewport = page.getViewport({ scale });

          // Cache the viewport for consistent reuse
          viewportCache.current.set(pageNum, scaledViewport);

          debugLog.render(pageNum, 'Computed and cached viewport', {
            width: scaledViewport.width,
            height: scaledViewport.height,
            rotation: scaledViewport.rotation,
            scale: scale,
            baseScale: baseScale,
            devicePixelRatio: devicePixelRatio,
            availableWidth: availableWidth,
            defaultMaxWidth: defaultMaxWidth,
            transform: scaledViewport.transform,
          });
        }

        // Get context before setting canvas dimensions
        context = canvas.getContext('2d');
        if (!context) {
          debugLog.canvas(pageNum, 'Failed to get 2D context');
          return;
        }

        // Log current canvas state before changes
        debugLog.canvas(pageNum, 'Pre-render canvas state', {
          currentWidth: canvas.width,
          currentHeight: canvas.height,
          styleWidth: canvas.style.width,
          styleHeight: canvas.style.height,
        });

        // Set canvas internal dimensions at high resolution
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        // Set CSS display dimensions (without device pixel ratio scaling)
        const displayWidth = scaledViewport.width / (window.devicePixelRatio || 1);
        const displayHeight = scaledViewport.height / (window.devicePixelRatio || 1);
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;

        debugLog.canvas(pageNum, 'Set canvas dimensions', {
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          styleWidth: canvas.style.width,
          styleHeight: canvas.style.height,
          displayWidth: displayWidth,
          displayHeight: displayHeight,
        });

        // Complete canvas context cleanup and reset
        context.save();

        // Clear the canvas completely
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Set white background
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Reset transform matrix to identity to ensure consistent rendering
        context.setTransform(1, 0, 0, 1, 0, 0);
        debugLog.canvas(pageNum, 'Reset canvas transform to identity matrix');

        const renderContext = {
          canvasContext: context,
          canvas: canvas,
          viewport: scaledViewport,
        };

        debugLog.render(pageNum, 'Starting PDF.js render');
        const renderStartTime = performance.now();

        // Wait for render to complete
        await page.render(renderContext).promise;

        // Restore context state
        context.restore();

        const renderEndTime = performance.now();
        debugLog.render(pageNum, 'Render completed successfully', {
          duration: `${(renderEndTime - renderStartTime).toFixed(2)}ms`,
        });

        // Store page display dimensions for overlay alignment
        setPageDimensions((prev) => new Map([...prev, [pageNum, { width: displayWidth, height: displayHeight }]]));

        // Update rendered pages state
        setRenderedPages((prev) => new Set([...prev, pageNum]));
        debugLog.render(pageNum, 'Added to rendered pages set');
      } catch (error) {
        debugLog.render(pageNum, 'Render error', error);
      } finally {
        context?.restore();
        // Remove from rendering set regardless of success or failure
        setRenderingPages((prev) => {
          const newSet = new Set(prev);
          newSet.delete(pageNum);
          return newSet;
        });
        debugLog.render(pageNum, 'Removed from rendering set');
      }
    },
    [pdfDocument, renderingPages, renderedPages],
  );

  // Process render queue sequentially to prevent race conditions
  const processRenderQueue = useCallback(async () => {
    if (isProcessingQueue || renderQueue.length === 0 || !pdfDocument) {
      return;
    }

    setIsProcessingQueue(true);
    debugLog.queue('Starting queue processing', { queueLength: renderQueue.length });

    const zeroIndexedPageToRender = zeroIndexedPage(renderQueue[0]);
    const pageToRender = oneIndexedPage(zeroIndexedPageToRender + 1);
    const canvas = canvasRefs.current.get(zeroIndexedPageToRender);

    if (canvas && !renderedPages.has(zeroIndexedPageToRender) && !renderingPages.has(zeroIndexedPageToRender)) {
      debugLog.queue(`Processing page ${pageToRender}`);

      try {
        await renderPageInternal(zeroIndexedPageToRender, canvas);
        debugLog.queue(`Successfully processed page ${pageToRender}`);
      } catch (error) {
        debugLog.queue(`Failed to process page ${pageToRender}`, error);
      }
    } else {
      debugLog.queue(`Skipping page ${pageToRender} - already rendered or canvas not ready`);
    }

    // Remove processed page from queue
    setRenderQueue((prev) => prev.slice(1));
    setIsProcessingQueue(false);
  }, [isProcessingQueue, renderQueue, pdfDocument, renderedPages, renderingPages, renderPageInternal]);

  // Process queue when it changes
  useEffect(() => {
    processRenderQueue();
  }, [processRenderQueue]);

  // Add page to render queue
  const queuePageRender = useCallback((pageNum: number) => {
    setRenderQueue((prev) => {
      if (!prev.includes(pageNum)) {
        debugLog.queue(`Adding page ${pageNum} to queue`);
        return [...prev, pageNum];
      }
      return prev;
    });
  }, []);

  // Load PDF with PDF.js
  useEffect(() => {
    const loadPDF = async () => {
      if (isLoadingPdf.current) {
        return;
      }

      try {
        isLoadingPdf.current = true;
        debugLog.render(-1, 'Starting PDF load', { url: basePdfUrl });
        setPdfLoaded(false);
        setPdfError(false);
        setRenderedPages(new Set());
        setRenderingPages(new Set());
        setRenderQueue([]);
        setIsProcessingQueue(false);
        canvasRefs.current.clear();
        viewportCache.current.clear();
        renderPromises.current.clear();
        setPageDimensions(new Map());

        // See https://github.com/mozilla/pdf.js/pull/19923#issuecomment-3038495101
        const task = pdfjsLib.getDocument({
          url: basePdfUrl,
          wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/wasm/`,
        });
        loadingTask.current = task;
        const pdf = await task.promise;

        setPdfDocument(pdf);
        setPageCount(pdf.numPages);
        setPdfLoaded(true);
        debugLog.render(-1, 'PDF loaded successfully', { numPages: pdf.numPages });
      } catch (error) {
        debugLog.render(-1, 'PDF load error', error);
        console.error('Error loading PDF:', error); // Keep error logging in production
        Sentry.captureException(error);
        setPdfError(true);
        setPdfLoaded(false);
      } finally {
        isLoadingPdf.current = false;
      }
    };

    if (basePdfUrl) {
      loadPDF();
    }
    return () => {
      if (!isLoadingPdf.current) {
        loadingTask.current?.destroy();
        loadingTask.current = null;
      }
    };
  }, [basePdfUrl, setPageCount, pdfjsLib]);

  if (pdfError) {
    return (
      <Container>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <Typography styledAs="bodyLargeDMSans" color="var(--error-red)">
            Failed to load PDF form. Please try again later.
          </Typography>
        </div>
      </Container>
    );
  }
  return (
    <PDFContainer ref={containerRef}>
      <PDFScrollContainer data-pdf-scroll-container="true" ref={scrollContainerRef}>
        {!pdfLoaded && <LoadingSpinner />}

        {pdfLoaded && pageCount > 0 && (
          <PDFContentArea>
            {Array.from({ length: pageCount }, (_, pageIndex) => {
              const zeroIndexedPageIndex = zeroIndexedPage(pageIndex);
              return (
                <PageContainer
                  key={zeroIndexedPageIndex}
                  $pageNumber={pageIndex}
                  $isFirst={pageIndex === 0}
                  $width={pageDimensions.get(zeroIndexedPageIndex)?.width}
                  $height={pageDimensions.get(zeroIndexedPageIndex)?.height}
                >
                  <PageCanvas
                    ref={(canvas) => {
                      if (
                        canvas &&
                        pdfDocument &&
                        !renderedPages.has(zeroIndexedPageIndex) &&
                        !renderingPages.has(zeroIndexedPageIndex)
                      ) {
                        // Store canvas reference and queue for rendering
                        canvasRefs.current.set(zeroIndexedPageIndex, canvas);
                        debugLog.canvas(pageIndex, 'Canvas ref set, queueing for render');

                        // Use timeout to ensure canvas is fully mounted in DOM
                        setTimeout(() => {
                          queuePageRender(pageIndex);
                        }, 10);
                      }
                    }}
                  />
                  {renderOverlay(zeroIndexedPageIndex, pageDimensions.get(zeroIndexedPageIndex))}
                </PageContainer>
              );
            })}
          </PDFContentArea>
        )}
      </PDFScrollContainer>
    </PDFContainer>
  );
}
